import { useNode } from "@craftjs/core";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateDocumentStyles } from "../../../../redux/reducers/PrevStylesCopy/CodeblockDefaultStyles";
import { ASSETS } from "../../../../utils/config";
import { sliderToPercentage } from "../../../../utils/helpers";
import CustomTabs from "../../Shared/CustomTabs";
import MediaSelection from "../../Shared/MediaSelection";
import Layout from "./Layout";
import List from "./List";

const DocumentSettings = () => {
  const {
    actions: { setProp },
    isApplyMarginOnAllSides,
    margin,
    allSideMargin,
    documents,
    shallowDocuments,
    columns,
    showThumbnail,
    previewStyle,
  } = useNode((node) => ({
    isApplyMarginOnAllSides: node.data.props.isApplyMarginOnAllSides,
    margin: node.data.props.margin,
    allSideMargin: node.data.props.allSideMargin,
    showThumbnail: node.data.props.showThumbnail,
    documents: node.data.props.documents,
    columns: node.data.props.columns,
    previewStyle: node.data.props.previewStyle,
  }));

  const dispatch = useDispatch();
  const { document: documentProps } = useSelector((state) => state.codeblockStyles);
  const [selectedTab, setSelectedTab] = useState("0");

  // Update default props in reducer when settings change
  const updateDefaultDocumentProps = (updatedProps) => {
    const updatedDocumentProps = {
      ...documentProps,
      ...updatedProps
    };
    dispatch(updateDocumentStyles(updatedDocumentProps));
  };

  const handleAdjustment = (side, operation) => {
    const newValue =
      operation === "increase" ? margin[side] + 1 : margin[side] - 1;
    if (newValue >= 0) handleChange("margin", newValue, side);
  };

  const handleChange = (type, value, side) => {
    setProp((props) => {
      switch (type) {
        case "margin":
          if (value >= 0 && value <= 100) {
            props.margin[side] = value;
            const updatedMargin = { ...props.margin };
            updateDefaultDocumentProps({ margin: updatedMargin });
          }
          break;
        case "allSideMargin":
          props.allSideMargin = value;
          updateDefaultDocumentProps({ allSideMargin: value });
          break;
        case "marginCheckbox":
          props.isApplyMarginOnAllSides = !props.isApplyMarginOnAllSides;
          updateDefaultDocumentProps({ isApplyMarginOnAllSides: !props.isApplyMarginOnAllSides });
          break;
        case "documents":
          const existingIndex = props.documents.findIndex(
            (doc) => doc.id === value.id
          );

          if (existingIndex !== -1) {
            props.documents[existingIndex] = {
              ...props.documents[existingIndex],
              ...value,
            };
          } else {
            props.documents.push(value);
          }
          // Don't update default for individual documents
          break;
        case "newDocuments":
          props.documents = value;
          // Don't update default for document list
          break;
        case "showThumbnail":
          props.showThumbnail = !props.showThumbnail;
          updateDefaultDocumentProps({ showThumbnail: !props.showThumbnail });
          break;
        case "columns":
          props.columns = value;
          updateDefaultDocumentProps({ columns: value });
          break;
      }
    }, 500);
  };

  // DL-049: preview style is set immediately (no debounce) so the segmented
  // control feels responsive. Block-local prop only — not written to the shared
  // reducer default.
  const handlePreviewStyleChange = (value) => {
    setProp((props) => {
      props.previewStyle = value;
    });
  };

  const handleTabChange = (e, newVal) => {
    setSelectedTab(newVal);
  };

  const tabOptions = new Map([
    [
      "0",
      {
        label: `List ${documents.length ? "(" + documents.length + ")" : ""}`,
        component: (
          <List
            documents={documents}
            setDocuments={(documents) =>
              handleChange("newDocuments", documents)
            }
            shallowDocs={shallowDocuments}
            onClickAdd={() => setSelectedTab("1")}
          />
        ),
      },
    ],
    [
      "1",
      {
        label: "Add Documents",
        component: (
          <MediaSelection
            type="documents"
            handleChange={handleChange}
            categories={["Upload", "Embed URL","Recent"]}
            media={documents}
            setMedia={(documents) => handleChange("newDocuments", documents)}
          />
        ),
      },
    ],
    [
      "2",
      {
        label: "Layout",
        disabled:documents.length==0,
        component: (
          <Layout
            setProp={setProp}
            margin={margin}
            isApplyMarginOnAllSides={isApplyMarginOnAllSides}
            allSideMargin={allSideMargin}
            showThumbnail={showThumbnail}
            handleAdjustment={handleAdjustment}
            handleChange={handleChange}
            columns={columns}
            previewStyle={previewStyle}
            onPreviewStyleChange={handlePreviewStyleChange}
          />
        ),
      },
    ],
  ]);

  return (
    <>
      <CustomTabs
        selectedTab={selectedTab}
        handleTabChange={handleTabChange}
        options={tabOptions}
      />
    </>
  );
};

export default DocumentSettings;
